<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page pi-aiu">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="assistant"/>
        <div class="pi-aiu__head">
          <pi-assistant-nav current="usage"/>
          <pi-export-screen :screen="screenName" :filters="questionFilters" :period="exportPeriodValue" :disabled="!request.loaded || !!request.error"/>
        </div>
        <p class="pi-page__lead">{{ lead }}</p>
        <p v-if="priceLine" class="pi-muted pi-aiu__price">{{ priceLine }}</p>

        <div class="pi-aiu__filters" role="group" aria-label="Filtros do consumo da IA">
          <div class="pi-toolbar__field">
            <span class="pi-aiu__label"><span :id="ids.period">Período</span><pi-definition-tooltip class="pi-aiu__info" info-key="assistant.usage.period"
                                                                                                  label="Período"/></span>
            <div class="pi-toggle" role="group" :aria-labelledby="ids.period">
              <button v-for="preset in presets" :key="preset.value" type="button" class="pi-toggle__btn"
                      :class="{ 'is-on': !customOpen && period.preset === preset.value }"
                      :aria-pressed="!customOpen && period.preset === preset.value ? 'true' : 'false'"
                      @click="choosePreset(preset.value)">{{ preset.label }}</button>
              <button type="button" class="pi-toggle__btn" :class="{ 'is-on': customOpen }" :aria-pressed="customOpen ? 'true' : 'false'"
                      @click="openCustom">Personalizado</button>
            </div>
          </div>
          <date-range-filter v-if="customOpen" label="Intervalo" :from="filters.from" :to="filters.to"
                             @update:from="setRange($event, filters.to)" @update:to="setRange(filters.from, $event)"/>
          <filter-select label="Pessoa" :value="filters.userId" :options="userOptions" all-label="Todas" @input="setFilter('userId', $event)">
            <pi-definition-tooltip slot="info" class="pi-aiu__info" info-key="assistant.usage.personFilter" label="Pessoa"/>
          </filter-select>
          <filter-select label="Agente usado" :value="filters.agent" :options="agentOptions" all-label="Todos" @input="setFilter('agent', $event)">
            <pi-definition-tooltip slot="info" class="pi-aiu__info" info-key="assistant.usage.agentFilter" label="Agente usado"/>
          </filter-select>
          <filter-select label="Status" :value="filters.status" :options="statusOptions" all-label="Todos" @input="setFilter('status', $event)">
            <pi-definition-tooltip slot="info" class="pi-aiu__info" info-key="assistant.usage.statusFilter" label="Status"/>
          </filter-select>
          <div class="pi-toolbar__field">
            <span><label class="pi-toolbar__text" :for="ids.search">Buscar na pergunta</label><pi-definition-tooltip class="pi-aiu__info" info-key="assistant.usage.search" label="Buscar na pergunta"/></span>
            <input :id="ids.search" v-model="searchInput" type="search" class="form-control pi-input" :maxlength="searchMax"
                   placeholder="Parte da pergunta" @input="onSearchInput" @keydown.enter.prevent="applySearch">
          </div>
          <button v-if="filtersActive" type="button" class="pi-btn pi-btn--ghost" @click="clearFilters">Limpar filtros</button>
        </div>
        <p class="pi-muted pi-aiu__period">{{ periodText }}</p>
        <p v-if="period.error" class="pi-warn" role="alert">{{ period.error }} Mostrando os últimos {{ period.days }} dias.</p>

        <pi-data-state :loading="request.loading" :error="request.error" :envelope="request.envelope" :empty="isEmpty"
                       :empty-message="emptyMessage" :show-footer="false" loading-text="Carregando o consumo da IA…" @retry="load(true)">
          <ul class="pi-stats pi-aiu__cards">
            <li v-for="card in cards" :key="card.key" class="pi-stat">
              <span class="pi-stat__head">
                <span class="pi-stat__label">{{ card.label }}</span>
                <pi-definition-tooltip :info-key="card.infoKey" :label="card.label"/>
              </span>
              <span class="pi-stat__value">{{ card.value }}</span>
              <span class="pi-stat__sub">{{ card.sub }}</span>
            </li>
          </ul>

          <div class="row">
            <section class="col-xs-12 col-lg-6 pi-section" aria-labelledby="pi-aiu-days">
              <div class="pi-section__head">
                <h2 id="pi-aiu-days" class="pi-section__title">Por dia (Brasília)</h2>
                <pi-export-menu title="Por dia" :screen="screenName" :columns="dayColumns" :rows="dayExport" :envelope="request.envelope"
                                :filters="exportFilters" :period="exportPeriodValue" :disabled="!days.length"/>
              </div>
              <pi-bar-list :rows="bars" empty-text="Sem perguntas no período."/>
              <div v-if="days.length" class="pi-table-wrap pi-aiu__days">
                <table class="pi-table">
                  <caption class="pi-sr">Perguntas, tokens e custo por dia</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in dayHeaders" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="day in days" :key="day.key">
                    <td>{{ day.dateText }}</td>
                    <td class="is-num">{{ day.questionsText }}</td>
                    <td class="is-num">{{ day.usersText }}</td>
                    <td class="is-num">{{ day.inputText }}</td>
                    <td class="is-num">{{ day.cachedText }}</td>
                    <td class="is-num">{{ day.outputText }}</td>
                    <td class="is-num">{{ day.costUsdText }}<span class="pi-table__sub">{{ day.costBrlText }}</span></td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <div class="col-xs-12 col-lg-6">
              <section class="pi-section" aria-labelledby="pi-aiu-users">
                <div class="pi-section__head">
                  <h2 id="pi-aiu-users" class="pi-section__title">Por pessoa</h2>
                  <pi-export-menu title="Por pessoa" :screen="screenName" :columns="userColumns" :rows="userExport" :envelope="request.envelope"
                                  :filters="exportFilters" :period="exportPeriodValue" :confirm-text="peopleConfirm" :disabled="!users.length"/>
                </div>
                <div v-if="users.length" class="pi-table-wrap">
                  <table class="pi-table">
                    <caption class="pi-sr">Perguntas e custo por pessoa</caption>
                    <thead>
                    <tr>
                      <pi-th v-for="column in userHeaders" :key="column.key" :column="column"/>
                      <th scope="col"><span class="pi-sr">Filtrar</span></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="user in users" :key="user.key" :class="{ 'pi-row-selected': filters.userId === user.id }">
                      <td>{{ user.name }}<span v-if="user.email" class="pi-table__sub">{{ user.email }}</span></td>
                      <td class="is-num">{{ user.questionsText }}</td>
                      <td class="is-num">{{ user.costText }}<span class="pi-table__sub">{{ user.costBrlText }}</span></td>
                      <td class="is-num">{{ user.avgCostText }}</td>
                      <td>{{ user.lastText }}</td>
                      <td>
                        <button type="button" class="pi-aiu__link-btn" :aria-pressed="filters.userId === user.id ? 'true' : 'false'"
                                :aria-label="userFilterLabel(user)" @click="toggleUser(user.id)">{{ filters.userId === user.id ? 'Todas' : 'Só esta' }}</button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="pi-muted">Sem perguntas no período.</p>
              </section>

              <section class="pi-section" aria-labelledby="pi-aiu-agents">
                <div class="pi-section__head">
                  <h2 id="pi-aiu-agents" class="pi-section__title">Por agente</h2>
                  <pi-export-menu title="Por agente" :screen="screenName" :columns="agentColumns" :rows="agentExport" :envelope="request.envelope"
                                  :filters="exportFilters" :period="exportPeriodValue" :disabled="!agentRows.length"/>
                </div>
                <div v-if="agentRows.length" class="pi-table-wrap">
                  <table class="pi-table">
                    <caption class="pi-sr">Perguntas, custo, tempo e utilidade por agente</caption>
                    <thead>
                    <tr>
                      <pi-th v-for="column in agentHeaders" :key="column.key" :column="column"/>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="row in agentRows" :key="row.key">
                      <td>{{ row.label }}</td>
                      <td class="is-num">{{ row.questionsText }}</td>
                      <td class="is-num">{{ row.costText }}</td>
                      <td class="is-num">{{ row.avgCostText }}</td>
                      <td class="is-num">{{ row.latencyText }}</td>
                      <td class="is-num">{{ row.usefulText }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="pi-muted">Sem perguntas no período.</p>
              </section>
            </div>
          </div>

          <section class="pi-section pi-aiu__list" aria-labelledby="pi-aiu-list">
            <div class="pi-section__head">
              <h2 id="pi-aiu-list" class="pi-section__title">Perguntas</h2>
              <pi-export-menu title="Perguntas" :screen="screenName" :columns="questionColumns" :source="questionSource" :filters="questionFilters"
                              :period="exportPeriodValue" :confirm-text="peopleConfirm" :disabled="!items.length"/>
              <p class="pi-section__hint pi-aiu__hint">{{ countText }} · clique na pergunta para ver a entrada, a saída, as chamadas ao modelo e o custo</p>
            </div>
            <framed-table>
              <thead>
              <tr>
                <template v-for="column in questionHeaders">
                  <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="sort" @sort="sortBy"/>
                  <pi-th v-else :key="column.key" :column="column"/>
                </template>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in items" :key="item.key" :class="{ 'pi-row-selected': detailId === item.id }">
                <td class="pi-aiu__nowrap">{{ item.askedDateText }}<span v-if="item.askedTimeText" class="pi-table__sub">{{ item.askedTimeText }}</span></td>
                <td class="pi-aiu__who">{{ item.userName }}<span v-if="item.userEmail" class="pi-table__sub">{{ item.userEmail }}</span></td>
                <td class="pi-aiu__question">
                  <button type="button" class="pi-aiu__open" :title="item.questionFull" @click="openDetail(item.id, $event)">
                    <span class="pi-sr">Detalhar a pergunta #{{ item.id }}: </span>{{ item.question }}
                  </button>
                  <span v-if="item.headline" class="pi-table__sub pi-aiu__headline">{{ item.headline }}</span>
                </td>
                <td class="pi-aiu__agent">{{ item.agentText }}</td>
                <td class="pi-aiu__status">
                  <span class="pi-badge" :class="'pi-badge--' + item.status.tone">{{ item.status.label }}</span>
                  <span v-if="item.unverifiedText" class="pi-table__sub pi-aiu__unverified">{{ item.unverifiedText }}</span>
                </td>
                <td class="is-num" :title="item.tokensDetail">{{ item.totalText }}<span class="pi-sr">: {{ item.tokensDetail }}</span></td>
                <td class="is-num">{{ item.costUsdText }}<span class="pi-table__sub">{{ item.costBrlText }}</span></td>
                <td class="is-num">{{ item.latencyText }}</td>
                <td>
                  <span v-if="item.feedback" class="pi-badge" :class="'pi-badge--' + item.feedback.tone">{{ item.feedback.label }}</span>
                  <span v-else class="pi-table__sub">—</span>
                </td>
              </tr>
              </tbody>
              <table-pager slot="footer" :total="total" :page="page" :page-size="pageSize"
                           @update:page="changePage" @update:pageSize="changePageSize"/>
            </framed-table>
          </section>
        </pi-data-state>

        <div v-if="detailId" class="pi-aiu__backdrop" aria-hidden="true" @click="closeDetail"></div>
        <aside v-if="detailId" ref="drawer" class="pi-aiu__drawer" role="dialog" aria-modal="true" aria-labelledby="pi-aiu-detail-title"
               @keydown="onDrawerKeydown">
          <div class="pi-aiu__drawer-head">
            <h2 id="pi-aiu-detail-title" class="pi-section__title">{{ detailTitle }}</h2>
            <button ref="close" type="button" class="pi-aiu__close" aria-label="Fechar o detalhe" @click="closeDetail">×</button>
          </div>
          <pi-data-state :loading="detail.loading" :error="detail.error" :envelope="detail.envelope" :show-footer="false"
                         loading-text="Carregando a pergunta…" @retry="loadDetail(true)">
            <pi-assistant-usage-detail v-if="detail.loaded" :detail="detailView"/>
          </pi-data-state>
        </aside>

        <pi-page-footer :envelopes="[]"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import framedTable from "../../components/default/framedTable.vue";
import tablePager from "../../components/default/tablePager.vue";
import filterSelect from "../../components/default/filterSelect.vue";
import dateRangeFilter from "../../components/default/dateRangeFilter.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piSortTh from "../../components/pi/piSortTh.vue";
import piTh from "../../components/pi/piTh.vue";
import piBarList from "../../components/pi/piBarList.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piAssistantNav from "../../components/pi/piAssistantNav.vue";
import piAssistantUsageDetail from "../../components/pi/piAssistantUsageDetail.vue";
import { loadAssistantAgents, loadAssistantUsage, loadAssistantUsageDetail } from "../../components/pi/piApi.js";
import { blankRequest, cancelRequest, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { sessionStore } from "../../components/pi/piNavigation.js";
import { formatDateFull, formatInteger } from "../../components/pi/piFormat.js";
import { nextSort, toQueryString } from "../../components/pi/piQuery.js";
import { pageOf } from "../../components/pi/piTable.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportProvide } from "../../components/pi/piExport.js";
import { normalizeAgents } from "../../components/pi/piAssistantModel.js";
import { PI_ASSISTANT_USAGE } from "../../../../urlConfig";
import {
  REVENUE_AGENT, USAGE_AGENT_COLUMNS, USAGE_DAY_COLUMNS, USAGE_FILTER_DEFAULTS, USAGE_LEAD, USAGE_PEOPLE_CONFIRM, USAGE_PRESETS, USAGE_QUESTION_COLUMNS,
  USAGE_SCREEN, USAGE_SEARCH_MAX, USAGE_USER_COLUMNS, agentList, agentOptions, agentRows, dayBars, dayList, dayRows, detailParam, hasUsageQuery, mergeUsers,
  priceText, readUsageQuery, sanitizeUsageFilters, statusOptions, usageCards, usageDetail, usageError, usageExportPeriod, usageFilterLines,
  usageFiltersActive, usageItems, usageParams, usagePeriod, usageSortLine, usageStateFromQuery, usageStateToQuery, usageTotal, userList, userRows,
  withUsageQuery, writeUsageQuery,
} from "../../components/pi/piAssistantUsageModel.js";

const SEARCH_DELAY_MS = 350;
const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex='-1'])";

const sameQuery = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export default {
  name: "pi-assistant-usage",
  components: {
    boxDefault, framedTable, tablePager, filterSelect, dateRangeFilter, piTabs, piDataState, piPageFooter, piSortTh, piTh, piBarList, piDefinitionTooltip,
    piExportMenu, piExportScreen, piAssistantNav, piAssistantUsageDetail,
  },
  provide: exportProvide,
  data() {
    const query = (this.$route && this.$route.query) || {};
    const saved = hasUsageQuery(query) ? null : readUsageQuery(sessionStore());
    const state = usageStateFromQuery(saved || query);
    return {
      ids: { period: "pi-aiu-period", search: "pi-aiu-search" },
      lead: USAGE_LEAD,
      presets: USAGE_PRESETS,
      searchMax: USAGE_SEARCH_MAX,
      screenName: USAGE_SCREEN,
      peopleConfirm: USAGE_PEOPLE_CONFIRM,
      dayColumns: USAGE_DAY_COLUMNS,
      userColumns: USAGE_USER_COLUMNS,
      agentColumns: USAGE_AGENT_COLUMNS,
      questionColumns: USAGE_QUESTION_COLUMNS,
      dayHeaders: screenColumns(USAGE_DAY_COLUMNS),
      userHeaders: screenColumns(USAGE_USER_COLUMNS),
      agentHeaders: screenColumns(USAGE_AGENT_COLUMNS),
      questionHeaders: screenColumns(USAGE_QUESTION_COLUMNS),
      statusOptions: statusOptions(),
      filters: state.filters,
      customOpen: !!(state.filters.from || state.filters.to),
      searchInput: state.filters.search,
      searchTimer: null,
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      restored: !!saved,
      request: blankRequest(),
      detail: blankRequest(),
      detailId: 0,
      returnFocus: null,
      knownUsers: [],
      pricing: null,
      agentsList: null,
    };
  },
  computed: {
    period() {
      return usagePeriod(this.filters, new Date());
    },
    periodText() {
      return "De " + formatDateFull(this.period.from) + " a " + formatDateFull(this.period.to) + ", hoje incluído (dia de Brasília).";
    },
    params() {
      return usageParams(this.filters, { page: this.page, pageSize: this.pageSize, sort: this.sort.sort, desc: this.sort.desc }, new Date());
    },
    apiKey() {
      return toQueryString(this.params);
    },
    usageQuery() {
      return usageStateToQuery({ filters: this.filters, sort: this.sort, page: this.page, pageSize: this.pageSize });
    },
    usageQueryKey() {
      return JSON.stringify(this.usageQuery);
    },
    routeUsageKey() {
      return JSON.stringify(usageStateToQuery(usageStateFromQuery(this.$route && this.$route.query)));
    },
    agentOptions() {
      return agentOptions(this.agentsList);
    },
    payload() {
      return payloadOf(this.request.envelope);
    },
    totals() {
      return this.payload.totals || {};
    },
    cards() {
      return usageCards(this.totals);
    },
    days() {
      return dayRows(this.payload.byDay);
    },
    dayExport() {
      return dayList(this.payload.byDay);
    },
    userExport() {
      return userList(this.payload.byUser);
    },
    agentExport() {
      return agentList(this.payload.byAgent);
    },
    exportFilters() {
      return usageFilterLines(this.filters, { users: this.userOptions, agents: this.agentOptions }, new Date());
    },
    questionFilters() {
      return this.exportFilters.concat([usageSortLine(this.sort)]);
    },
    exportPeriodValue() {
      return usageExportPeriod(this.filters, new Date());
    },
    questionSource() {
      return { path: PI_ASSISTANT_USAGE, query: this.params, pick: (data) => (data ? data.rows : null) };
    },
    bars() {
      return dayBars(this.payload.byDay);
    },
    users() {
      return userRows(this.payload.byUser);
    },
    agentRows() {
      return agentRows(this.payload.byAgent);
    },
    items() {
      return usageItems(this.payload.rows && this.payload.rows.items);
    },
    total() {
      return usageTotal(this.payload.rows);
    },
    countText() {
      return formatInteger(this.total) + (this.total === 1 ? " pergunta" : " perguntas");
    },
    userOptions() {
      return this.knownUsers.map((user) => ({ value: user.value, label: user.label }));
    },
    filtersActive() {
      return usageFiltersActive(this.filters);
    },
    isEmpty() {
      return this.request.loaded && !this.totals.questions && !this.items.length;
    },
    emptyMessage() {
      return this.filtersActive ? "Nenhuma pergunta com esses filtros no período." : "Ninguém perguntou ao assistente no período.";
    },
    priceLine() {
      return priceText(this.pricing);
    },
    detailView() {
      return usageDetail(payloadOf(this.detail.envelope));
    },
    detailTitle() {
      return "Pergunta #" + this.detailId;
    },
  },
  watch: {
    apiKey() {
      this.load(false);
    },
    usageQueryKey() {
      writeUsageQuery(sessionStore(), this.usageQuery);
      this.syncRoute();
    },
    routeUsageKey(value) {
      if (value !== this.usageQueryKey) this.applyState(usageStateFromQuery(this.$route.query));
    },
    "$route.query.detalhe"() {
      const id = detailParam(this.$route && this.$route.query);
      if (id !== this.detailId) this.showDetail(id);
    },
  },
  created() {
    const agents = this.loadAgents();
    if (this.filters.agent === REVENUE_AGENT) {
      this.request.loading = true;
      agents.then(() => this.load(false));
    } else {
      this.load(false);
    }
    const id = detailParam(this.$route && this.$route.query);
    if (id) this.showDetail(id);
    if (this.restored) this.syncRoute();
    else if (hasUsageQuery(this.$route && this.$route.query)) writeUsageQuery(sessionStore(), this.usageQuery);
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.request, key, force)) return;
      runRequest(this.request, () => loadAssistantUsage(this.params), (error) => usageError(error, "usage"), key).then((applied) => {
        if (!applied) return;
        const payload = payloadOf(this.request.envelope);
        this.knownUsers = mergeUsers(this.knownUsers, payload.byUser);
        const total = usageTotal(payload.rows);
        if (this.page > 1 && total > 0 && !usageItems(payload.rows && payload.rows.items).length) this.page = Math.max(1, Math.ceil(total / this.pageSize));
      });
    },
    loadAgents() {
      return loadAssistantAgents().then((envelope) => {
        const view = normalizeAgents(payloadOf(envelope));
        this.pricing = view.pricing;
        this.agentsList = view.agents;
        if (this.filters.agent && !this.agentOptions.some((option) => option.value === this.filters.agent)) this.setFilter("agent", "");
      }, () => {
        this.pricing = null;
      });
    },
    applyState(state) {
      this.filters = state.filters;
      this.customOpen = !!(state.filters.from || state.filters.to);
      this.searchInput = state.filters.search;
      this.sort = state.sort;
      this.page = state.page;
      this.pageSize = state.pageSize;
    },
    changeFilters(next) {
      this.filters = sanitizeUsageFilters(next);
      this.page = 1;
    },
    choosePreset(value) {
      this.customOpen = false;
      this.changeFilters(Object.assign({}, this.filters, { preset: value, from: "", to: "" }));
    },
    openCustom() {
      this.customOpen = true;
      if (!this.filters.from && !this.filters.to) {
        this.changeFilters(Object.assign({}, this.filters, { from: this.period.from, to: this.period.to }));
      }
    },
    setRange(from, to) {
      this.changeFilters(Object.assign({}, this.filters, { from: from || "", to: to || "" }));
    },
    setFilter(key, value) {
      const next = Object.assign({}, this.filters);
      next[key] = typeof value === "string" ? value : "";
      if (key === "agent" && next.agent && !this.agentOptions.some((option) => option.value === next.agent)) next.agent = "";
      this.changeFilters(next);
    },
    toggleUser(id) {
      this.setFilter("userId", this.filters.userId === id ? "" : id);
    },
    userFilterLabel(user) {
      return this.filters.userId === user.id ? "Mostrar as perguntas de todas as pessoas" : "Ver só as perguntas de " + user.name;
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applySearch, SEARCH_DELAY_MS);
    },
    applySearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = null;
      this.setFilter("search", this.searchInput);
    },
    clearFilters() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = null;
      this.searchInput = "";
      this.customOpen = false;
      this.changeFilters(Object.assign({}, USAGE_FILTER_DEFAULTS));
    },
    sortBy(column) {
      this.sort = nextSort(this.sort, column.key, column.startDescending);
      this.page = 1;
    },
    changePage(page) {
      this.page = pageOf({ page: page }, 1);
    },
    changePageSize(size) {
      this.pageSize = size;
      this.page = 1;
    },
    openDetail(id, event) {
      this.returnFocus = event && event.currentTarget ? event.currentTarget : (event && event.target ? event.target : null);
      this.showDetail(id);
      this.syncRoute();
    },
    showDetail(id) {
      this.detailId = id || 0;
      if (!this.detailId) {
        cancelRequest(this.detail);
        return;
      }
      this.loadDetail(false);
      this.$nextTick(() => {
        const close = this.$refs.close;
        if (close && typeof close.focus === "function") close.focus();
      });
    },
    loadDetail(force) {
      const id = this.detailId;
      if (!id || !shouldLoad(this.detail, String(id), force)) return;
      runRequest(this.detail, () => loadAssistantUsageDetail(id), (error) => usageError(error, "detail"), String(id));
    },
    closeDetail() {
      const target = this.returnFocus;
      this.returnFocus = null;
      this.showDetail(0);
      this.syncRoute();
      this.$nextTick(() => {
        if (target && typeof target.focus === "function" && document.body.contains(target)) target.focus();
      });
    },
    syncRoute() {
      if (!this.$router || !this.$route) return;
      const query = withUsageQuery(this.$route.query, this.usageQuery);
      if (this.detailId) query.detalhe = String(this.detailId);
      else delete query.detalhe;
      if (!sameQuery(query, this.$route.query)) this.$router.replace({ query: query }, () => {}, () => {});
    },
    onDrawerKeydown(event) {
      if (!event) return;
      if (event.key === "Escape" || event.key === "Esc") {
        event.preventDefault();
        this.closeDetail();
        return;
      }
      if (event.key !== "Tab" || !this.$refs.drawer) return;
      const nodes = Array.prototype.slice.call(this.$refs.drawer.querySelectorAll(FOCUSABLE));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-aiu__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px 16px;
}

.pi-aiu__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pi-aiu__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-aiu__hint {
  flex-basis: 100%;
}

.pi-aiu__price {
  margin: -8px 0 14px;
}

.pi-aiu__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 16px;
  margin-bottom: 6px;
}

.pi-aiu__filters .pi-toolbar__field .form-control {
  width: 200px;
}

.pi-aiu__period {
  margin-bottom: 14px;
}

.pi-aiu__cards .pi-stat {
  flex-basis: 160px;
}

.pi-aiu__days {
  max-height: 360px;
  margin-top: 12px;
  overflow-y: auto;
}

.pi-aiu__nowrap {
  white-space: nowrap;
}

.pi-aiu__question {
  min-width: 220px;
  max-width: 380px;
}

.pi-aiu__list th {
  white-space: nowrap;
}

.pi-aiu__who {
  min-width: 160px;
  max-width: 230px;
  overflow-wrap: anywhere;
}

.pi-aiu__agent {
  min-width: 110px;
}

.pi-aiu__status {
  min-width: 96px;
}

.pi-aiu__status .pi-badge {
  white-space: normal;
}

.pi-aiu__open {
  display: inline;
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.pi-aiu__open:hover {
  text-decoration: underline;
}

.pi-aiu__open:focus {
  outline: 2px solid #1b59f8;
  outline-offset: 2px;
}

.pi-aiu__headline {
  color: #4e5560;
}

.pi-aiu__unverified {
  color: #7a4f00;
}

.pi-aiu__link-btn {
  white-space: nowrap;
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font-size: 12px;
  cursor: pointer;
}

.pi-aiu__backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1040;
  background: rgba(17, 24, 39, 0.35);
}

.pi-aiu__drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  width: 760px;
  max-width: 100%;
  padding: 16px 20px 24px;
  overflow-y: auto;
  background: #ffffff;
  box-shadow: -4px 0 18px rgba(0, 0, 0, 0.18);
}

.pi-aiu__drawer-head {
  position: sticky;
  top: -16px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: -16px -20px 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #e6e7eb;
  background: #ffffff;
}

.pi-aiu__close {
  width: 32px;
  height: 32px;
  border: 1px solid #c5cad3;
  border-radius: 4px;
  background: #ffffff;
  color: #3a424e;
  font-size: 18px;
  line-height: 28px;
  cursor: pointer;
}

.pi-aiu__close:focus {
  outline: 2px solid #1b59f8;
  outline-offset: 2px;
}

@media (max-width: 767px) {
  .pi-aiu__filters .pi-toolbar__field .form-control {
    width: 100%;
  }

  .pi-aiu__drawer {
    padding: 12px 14px 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piAssistantUsage.vue